import { Box, Chip, Stack, Typography } from '@mui/material';
import { ColorPickerField } from '@scoutingops/ui';

import type { SiteThemePalette } from '@/types/database';
import { contrastRatio, meetsWcagAA } from '@/styles/theme/color-utils';
import { deriveThemeTokens } from '@/styles/theme/derive-tokens';
import { TwoColumn } from './TwoColumn';
import type { SiteStylesScreenProps } from './types';

const SWATCH_LABELS: { key: keyof SiteThemePalette; label: string; default: string }[] = [
  { key: 'lightest', label: 'Lightest', default: '#ffffff' },
  { key: 'light', label: 'Light', default: '#e8e2d8' },
  { key: 'accent', label: 'Accent', default: '#c98866' },
  { key: 'dark', label: 'Dark', default: '#4a5a58' },
  { key: 'darkest', label: 'Darkest', default: '#22292b' },
];

// Named by role, not by raw palette slot (was "Lightest 1"/"Light 1"/etc.)
// — a section assigned "Feature" should read the same regardless of which
// hex values back a unit's palette, matching the design-system review's
// call for semantic section-theme names over raw color labels.
const SECTION_THEMES: { name: string; bg: keyof SiteThemePalette; text: 'textOnLightest' | 'textOnLight' | 'textOnDark' | 'textOnDarkest' }[] = [
  { name: 'Default', bg: 'lightest', text: 'textOnLightest' },
  { name: 'Subtle', bg: 'light', text: 'textOnLight' },
  { name: 'Dark', bg: 'dark', text: 'textOnDark' },
  { name: 'Feature', bg: 'darkest', text: 'textOnDarkest' },
];

interface ContrastCheck {
  label: string;
  background: string;
  foreground: string;
  isLargeOrUi?: boolean;
}

export function ColorsScreen({ draft, updateTheme }: SiteStylesScreenProps) {
  const { palette } = draft.theme_config;
  const tokens = deriveThemeTokens(palette);

  const setPalette = (key: keyof SiteThemePalette, value: string) => {
    if (!value) return;
    updateTheme({ ...draft.theme_config, palette: { ...palette, [key]: value } });
  };

  const contrastChecks: ContrastCheck[] = [
    { label: 'Body text on background', background: palette.lightest, foreground: tokens.textOnLightest },
    { label: 'Text on Subtle surface', background: palette.light, foreground: tokens.textOnLight },
    { label: 'Text on Dark surface', background: palette.dark, foreground: tokens.textOnDark },
    { label: 'Text on Feature surface', background: palette.darkest, foreground: tokens.textOnDarkest },
    { label: 'Text on Accent buttons', background: palette.accent, foreground: tokens.textOnAccent, isLargeOrUi: true },
  ];

  return (
    <Stack spacing={2.5} sx={{ p: 2.5 }}>
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
          Palette
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The five colors used throughout your public site.
        </Typography>
      </Box>

      <TwoColumn
        controls={
          <>
            <Box sx={{ display: 'flex', height: 64, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
              {SWATCH_LABELS.map(({ key }) => (
                <Box key={key} sx={{ flex: 1, bgcolor: palette[key] }} />
              ))}
            </Box>
            <Stack spacing={2}>
              {SWATCH_LABELS.map(({ key, label, default: defaultValue }) => (
                <ColorPickerField
                  key={key}
                  label={label}
                  value={palette[key]}
                  fallbackColor={defaultValue}
                  onChange={(value) => setPalette(key, value)}
                />
              ))}
            </Stack>
          </>
        }
        preview={
          <Stack spacing={3}>
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                Contrast checks
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Based on the WCAG AA text-contrast standard, so this palette stays readable for every visitor.
              </Typography>
              <Stack spacing={1}>
                {contrastChecks.map((check) => {
                  const ratio = contrastRatio(check.background, check.foreground);
                  const passes = meetsWcagAA(ratio, check.isLargeOrUi);
                  return (
                    <Stack
                      key={check.label}
                      direction="row"
                      spacing={1.5}
                      sx={{ alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, borderRadius: 1.5, bgcolor: 'action.hover' }}
                    >
                      <Typography variant="body2">{check.label}</Typography>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          {ratio.toFixed(1)}:1
                        </Typography>
                        <Chip
                          size="small"
                          label={passes ? 'Passes AA' : 'Low contrast'}
                          color={passes ? 'success' : 'warning'}
                          variant={passes ? 'outlined' : 'filled'}
                        />
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                Section color themes
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Derived automatically from the palette above — assigning these to individual page sections is coming in a later update.
              </Typography>
              <Stack spacing={1}>
                {SECTION_THEMES.map((theme) => (
                  <Box
                    key={theme.name}
                    sx={{
                      bgcolor: palette[theme.bg],
                      color: tokens[theme.text],
                      borderRadius: 1.5,
                      px: 2,
                      py: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>{theme.name}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        }
      />
    </Stack>
  );
}
